import React, { useRef, useState, useEffect } from "react";
import "./candidateUpload.scss";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Alert, Snackbar } from "@mui/material";
import {
  fetchJobById,
  applyForJob,
} from "../../Redux/ActionCreators/jobsAction";

const CandidateUpload = () => {
  const [activeTab, setActiveTab] = useState("Personal info");
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resumeError, setResumeError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackSeverity, setSnackSeverity] = useState("error");
  const [snackMessage, setSnackMessage] = useState("");
  const [isHover, setIsHover] = useState(false);
  const { jobId } = useParams();
  const dispatch = useDispatch();
  const resumeInputRef = useRef(null);

  const job = useSelector((state) => state.jobs.latestJob);

  useEffect(() => {
    const fetchJob = async () => {
      if (jobId) {
        setLoading(true);
        await dispatch(fetchJobById(jobId));
        setLoading(false);
      }
    };
    fetchJob();
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleResumeClick = () => {
    resumeInputRef.current.click();
  };

  const handlePdfUpload = (e) => {
    const f = e.target.files[0];
    if (f) {
      setResume(f);
      setResumeError(false);
    }
  };

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required("First name is required"),
    last_name: Yup.string().required("last name is required"),
    email: Yup.string().required("Email Id is required"),
    phone_number: Yup.string().required("Phone number is required"),
  });

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      resume: null,
    },
    validationSchema,
    onSubmit: async (values, formikHelpers) => {
      const { setSubmitting, resetForm } = formikHelpers;

      if (!resume) {
        setResumeError(true);
        return;
      } else {
        setResumeError(false);
      }

      const formData = new FormData();
      formData.append("name", `${values.first_name} ${values.last_name}`);
      formData.append("email", values.email);
      formData.append("phone_number", values.phone_number);
      formData.append("resume", resume);
    try {
        setSubmitting(true);
        const response = await dispatch(applyForJob(jobId, formData));
        setSubmitting(false);
    
          if (response === 200) {
            showSnack(response?.data?.message || "Applying for job successful", "success");
            resetForm();
            setResume(null);
            setResumeError(false);
          } else {
            showSnack(response.message || "Applying for job failed", "error");
          }
        } catch (error) {
          showSnack(error?.message || "Unexpected error occurred Applying for job failed", "error");
        }
    },
  });

  const snackClosed = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackOpen(false);
  };

  const showSnack = (message, severity = "error") => {
    setSnackMessage(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  if (loading) {
    return (
      <div className="job-summary">
        <div className="loading-overlay">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading job summary...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="candidate-upload" style={{height:'985px'}}>
      <div className="candidate-upload-header">
        <div className="candidate-upload-title">
          <div className="company-logo">
            <img src="/images/gappeoai-sm-logo.svg" alt="" />
          </div>
          <div className="position-details">
            <h3>{job?.title}</h3>
            <div className="requirements">
              {job?.skills?.slice(0, 2).map((skills, index) => (
                <p className="candidate-skills" key={index}>
                  {skills}
                </p>
              ))}
              {job?.skills?.length > 2 && (
                <p className="candidate-skills more-skills">
                  +{job.skills.length - 2} more
                  <div className="skills-popup">
                    {job.skills.slice(2).map((s, idx, arr) => (
                      <span key={idx}>
                        {s}
                        {idx < arr.length - 1 && ", "}
                      </span>
                    ))}
                  </div>
                </p>
              )}
              {job?.salary && (
                <p style={{ background: "#DEF7EC", color: "#03543F" }}>
                  {job?.salary}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="candidate-job-description">
          <div className="job-description">
            <h4>Job details</h4>
            <div className="row">
              <div className="detail-item d-flex col-md-6 mb-2">
                <h6>Job Title :- </h6>&nbsp;
                {job?.title? <p>{job?.title}</p> : <p>N/A</p>}
              </div>
              <div className="detail-item d-flex col-md-6 mb-2">
                <h6>Company name :- </h6>&nbsp;
                {job?.company_name? <p>{job?.company_name}</p> : <p>N/A</p>}
              </div>
              <div className="detail-item d-flex col-md-6 mb-2">
                <h6>Employment type :- </h6>&nbsp;
                {job?.job_type?.name? <p>{job?.job_type?.name}</p> : <p>N/A</p>}
              </div>
              <div className="detail-item d-flex col-md-6 mb-2">
                <h6>Location :- </h6>&nbsp;
                {job?.country?.name? <p>{job?.country?.name},{job?.city?.name}</p> : <p>N/A</p>}
              </div>
              <div className="detail-item d-flex col-md-6 mb-2">
                <h6>Experience :- </h6>&nbsp;
                {job?.experience_required? <p>{job?.experience_required}</p> : <p>N/A</p>}
              </div>
              <div className="detail-item d-flex col-md-6 mb-2">
                <h6>Job qualifications :- </h6>&nbsp;
                {job?.qualifications? <p>{job?.qualifications}</p> : <p>N/A</p>}
              </div>
              <div className="detail-item col-md-12 mb-2">
                <h6>Job description :- </h6>
                <p>
                  {job?.description
                    ?.split("\n")
                    .map((line, index) =>
                      line.trim() !== "" ? <p key={index}>{line}</p> : null
                    )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="candidate-upload-body">
        <div className="tab-navigation">
          {["Personal info"].map((tab) => (
            <button
              key={tab}
              className={`tab-item ${activeTab === tab ? "active" : ""}`}
              onClick={() => handleTabChange(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="job-details-body">
          {activeTab === "Personal info" ? (
            <form onSubmit={formik.handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-section">
                    <label>
                      First Name <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      placeholder="first name"
                      value={formik.values.first_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={
                        formik.touched.first_name && formik.errors.first_name
                          ? "input-error"
                          : ""
                      }
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-section">
                    <label>
                      Last Name <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      placeholder="last name"
                      value={formik.values.last_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={
                        formik.touched.last_name && formik.errors.last_name
                          ? "input-error"
                          : ""
                      }
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-section">
                    <label>
                      Email ID <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={
                        formik.touched.email && formik.errors.email
                          ? "input-error"
                          : ""
                      }
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-section">
                    <label>
                      Phone number <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="phone_number"
                      placeholder="phone number"
                      value={formik.values.phone_number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={
                        formik.touched.phone_number &&
                        formik.errors.phone_number
                          ? "input-error"
                          : ""
                      }
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-section">
                    <label>Upload resume</label>
                    <div className="resume-upload">
                      <img src="/images/upload-gray.svg" alt="" />
                      <p>
                        <strong>Click to upload</strong> or drag and drop
                      </p>
                      <p>
                        <strong>Supported format: .word,.pdf</strong>
                      </p>
                      <button
                        className="resume-button"
                        type="button"
                        onClick={handleResumeClick}
                        disabled={formik.isSubmitting}
                        onMouseEnter={() => {
                          setIsHover(true);
                        }}
                        onMouseLeave={() => {
                          setIsHover(false);
                        }}
                      >
                        {isHover ? (
                          <img src="/images/cart-plus-icon-wight.svg" alt="" />
                        ) : (
                          <img src="/images/cart-plus-icon.svg" alt="" />
                        )}
                        &nbsp;&nbsp;Browse File
                      </button>
                      <input
                        ref={resumeInputRef}
                        type="file"
                        name="resume"
                        accept=".pdf,.docx"
                        onChange={handlePdfUpload}
                        style={{ display: "none" }}
                      />
                      {resume && (
                        <p>
                          Selected: <strong>{resume.name}</strong>
                        </p>
                      )}
                      {resumeError ? (
                        <p className="text-danger">Please Upload PDF resume</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
              <button
                className="btn mt-3"
                type="submit"
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting ? "Submitting..." : "Apply for Job"}
              </button>
            </form>
          ) : null}
        </div>
      </div>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={snackOpen}
        autoHideDuration={5000}
        onClose={snackClosed}
      >
        <Alert
          onClose={snackClosed}
          severity={snackSeverity}
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CandidateUpload;
